import axios from "axios";
import appconfig from "../../AppConfig";
import {
  ICart,
  IGatewayForm,
  IPaymentConfig,
  IPaymentReceipt,
} from "../../models";

const config = { baseURL: appconfig.baseURL };

/** Server messages are Serbian and meant for the customer. */
const errorText = (err: any): string =>
  err?.response?.data?.error ??
  "Došlo je do greške. Proverite internet vezu i pokušajte ponovo.";

export const getPaymentConfigApi = async (): Promise<IPaymentConfig> => {
  try {
    const response = await axios.get<any>("payment/config", config);
    return response.data as IPaymentConfig;
  } catch {
    return { mode: "off", recaptchaSiteKey: "", isTest: false };
  }
};

export interface ICardOrderResult {
  orderNumber: string;
  token: string;
  form: IGatewayForm;
}

export const createCardOrderApi = async (
  cart: ICart,
  expectedTotal: number,
  termsAccepted: boolean,
  recaptchaToken: string
): Promise<{ ok: true; data: ICardOrderResult } | { ok: false; error: string }> => {
  try {
    const response = await axios.post<any>(
      "payment/order",
      { ...cart, expectedTotal, termsAccepted, recaptchaToken },
      config
    );
    return { ok: true, data: response.data as ICardOrderResult };
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};

export const getPaymentStatusApi = async (
  token: string
): Promise<IPaymentReceipt | null> => {
  try {
    const response = await axios.get<any>(`payment/status/${token}`, config);
    return response.data as IPaymentReceipt;
  } catch {
    return null;
  }
};

export const retryPaymentApi = async (
  token: string
): Promise<{ ok: true; form: IGatewayForm } | { ok: false; error: string }> => {
  try {
    const response = await axios.post<any>(`payment/retry/${token}`, null, config);
    return { ok: true, form: response.data.form as IGatewayForm };
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};

export const switchToCodApi = async (
  token: string
): Promise<{ ok: true; receipt: IPaymentReceipt } | { ok: false; error: string }> => {
  try {
    const response = await axios.post<any>(`payment/switchToCod/${token}`, null, config);
    return { ok: true, receipt: response.data as IPaymentReceipt };
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};

/** Admin: sync pending attempts and captures done in Merchant Center. */
export const reconcilePaymentsApi = async (): Promise<number> => {
  try {
    const response = await axios.post<any>("payment/reconcile", null, config);
    return response.data?.changed ?? 0;
  } catch {
    return 0;
  }
};

export interface IAdminPaymentResult {
  ok: boolean;
  error?: string;
  paymentStatus?: string;
}

const adminAction = async (url: string, body: any): Promise<IAdminPaymentResult> => {
  try {
    const response = await axios.post<any>(url, body, config);
    return response.data as IAdminPaymentResult;
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};

/** Admin: charge the reserved money (PostAuth). A smaller amount is a partial capture. */
export const capturePaymentApi = (cartId: number, amount?: number) =>
  adminAction(`payment/capture/${cartId}`, amount === undefined ? {} : { amount });

/** Admin: cancel the reservation, or a capture made the same day. */
export const voidPaymentApi = (cartId: number) =>
  adminAction(`payment/void/${cartId}`, {});

/** Admin: refresh one order's payment status from the bank. */
export const syncPaymentApi = async (
  cartId: number
): Promise<{ status: string | null; error: string | null }> => {
  try {
    const response = await axios.post<any>(`payment/sync/${cartId}`, null, config);
    return {
      status: response.data?.paymentStatus ?? null,
      error: response.data?.error ?? null,
    };
  } catch (err) {
    return { status: null, error: errorText(err) };
  }
};
